import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Platform, useWindowDimensions } from 'react-native'

import {
  BottomSheetBackdrop,
  BottomSheetBackdropProps,
  BottomSheetModal,
  BottomSheetScrollView,
} from '@gorhom/bottom-sheet'
import { Icon, Pressable, Stack, XStack } from '@zet/design-system'
import { B26 } from '@zet/integration-flow/components/typography'

import {
  BottomSheetActions,
  BrandFilterList,
  FilterChips,
  FilterTabs,
  SortOptions,
} from './components'
import { useJfsFilterState } from './hooks/useJfsFilterState'

// Note: react-spring-bottom-sheet is dynamically imported for web only inside the component

// Remove default borders from react-spring-bottom-sheet
if (typeof document !== 'undefined' && Platform.OS === 'web') {
  const style = document.createElement('style')
  style.textContent = `
    [data-rsbs-header] {
      border-bottom: none !important;
      box-shadow: none !important;
    }
    [data-rsbs-footer] {
      border-top: none !important;
      box-shadow: none !important;
    }
  `
  document.head.appendChild(style)
}

export type DropDownFilterData = {
  key: string
  type: 'SUB_FILTER' | 'DROPDOWN' | 'QUICK_FILTER'
  displayText: string
  isMultiSelect?: boolean
  values?: {
    isDefault: boolean | undefined
    key: 'BRANDS'
    displayText: string
    isMultiSelect: boolean
    values?: {
      key: string
      displayText: string
    }[]
  }[]
}

export type DropDownFilterState = string[] | string | undefined

interface JfsFiltersBottomSheetProps {
  customerId?: string | number
  categoryId?: string | number
  data?: DropDownFilterData[]
  onDismiss?: () => void
  onChange?: (selectedFilters: DropDownFilterState) => void
}

const JfsFiltersBottomSheet = (
  { data, onDismiss }: JfsFiltersBottomSheetProps,
  ref: any,
) => {
  const { height } = useWindowDimensions()
  const bottomSheetModalRef = useRef<BottomSheetModal>(null)
  const [isVisible, setIsVisible] = useState(false)
  const [selectedTab, setSelectedTab] = useState(data?.[0]?.key || 'sort')
  const [WebBottomSheet, setWebBottomSheet] = useState<any>(null)

  const {
    localSort,
    setLocalSort,
    localFilters,
    toggleFilter,
    resetAll,
    applyFilters,
    resetToApplied,
  } = useJfsFilterState()

  const sortData = useMemo(() => data?.[0], [data])
  const filtersData = useMemo(() => data?.[1], [data])

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        {...props}
        disappearsOnIndex={-1}
        appearsOnIndex={0}
      />
    ),
    [],
  )

  const handleDismiss = useCallback(() => {
    resetToApplied()
    if (Platform.OS !== 'web') {
      bottomSheetModalRef.current?.dismiss()
    }
    setIsVisible(false)
    onDismiss?.()
  }, [resetToApplied, onDismiss])

  const handleResetAll = useCallback(() => {
    resetAll()
    if (Platform.OS !== 'web') {
      bottomSheetModalRef.current?.dismiss()
    }
    setIsVisible(false)
    onDismiss?.()
  }, [resetAll, onDismiss])

  const handleApply = useCallback(() => {
    applyFilters()
    if (Platform.OS !== 'web') {
      bottomSheetModalRef.current?.dismiss()
    }
    setIsVisible(false)
    onDismiss?.()
  }, [applyFilters, onDismiss])

  // Dynamically load web bottom sheet component
  useEffect(() => {
    if (Platform.OS === 'web' && !WebBottomSheet) {
      Promise.all([
        import('react-spring-bottom-sheet'),
        // @ts-ignore - CSS import for styling
        import('react-spring-bottom-sheet/dist/style.css').catch(() => null),
      ]).then(([module]) => {
        setWebBottomSheet(() => module.BottomSheet)
      })
    }
  }, [WebBottomSheet])

  // Initialize default sort if no sort is selected
  useEffect(() => {
    if (!localSort && sortData?.values) {
      const defaultSort = sortData.values.find((option) => option.isDefault)
      if (defaultSort) {
        setLocalSort(defaultSort.key)
      }
    }
  }, [sortData, localSort, setLocalSort])

  useImperativeHandle(
    ref,
    () => ({
      resetLocalState: resetAll,
      updateLcalState: (filterState: string) => setLocalSort(filterState),
      show: () => {
        if (Platform.OS !== 'web') {
          bottomSheetModalRef.current?.present()
        }
        setIsVisible(true)
      },
      hide: () => {
        if (Platform.OS !== 'web') {
          bottomSheetModalRef.current?.dismiss()
        }
        setIsVisible(false)
      },
    }),
    [resetAll, setLocalSort],
  )

  const renderFilterContent = () => {
    const selectedFilterIndex = filtersData?.values?.findIndex(
      (filter) => filter?.key === selectedTab,
    )
    const selectedFilterData =
      selectedFilterIndex !== undefined && selectedFilterIndex >= 0
        ? filtersData?.values?.[selectedFilterIndex]
        : undefined

    // Sort tab
    if (selectedTab === 'sort') {
      return (
        <SortOptions
          options={sortData?.values || []}
          selectedSort={localSort as string}
          onSortChange={setLocalSort}
        />
      )
    }

    // Brands tab (groupCode)
    if (selectedTab === 'groupCode' && selectedFilterData) {
      return (
        <BrandFilterList
          data={selectedFilterData as any}
          selectedFilters={
            Array.isArray(localFilters?.groupCode) ? localFilters.groupCode : []
          }
          onFilterChange={(brandKey, isSelected) => {
            toggleFilter('groupCode', brandKey)
          }}
        />
      )
    }

    // Other filter tabs (chips/pills)
    return (
      <FilterChips
        items={selectedFilterData?.values || []}
        selectedItems={
          Array.isArray(localFilters?.[selectedTab])
            ? (localFilters[selectedTab] as string[])
            : []
        }
        onToggle={(itemKey) => toggleFilter(selectedTab, itemKey)}
      />
    )
  }

  const filterTabs = useMemo(() => {
    const tabs: any[] = [sortData]
    if (filtersData?.values) {
      tabs.push(...filtersData.values)
    }
    return tabs.filter(Boolean)
  }, [sortData, filtersData])

  const filterCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    filterTabs.forEach((tab) => {
      if (tab.key !== 'sort') {
        const filterValues = localFilters?.[tab.key]
        counts[tab.key] = Array.isArray(filterValues) ? filterValues.length : 0
      }
    })
    return counts
  }, [filterTabs, localFilters])

  const Header = () => (
    <Stack>
      <XStack className="justify-between items-center pt-3 pb-2">
        <B26>Sort & filter</B26>
        <Pressable onPress={handleDismiss}>
          <Icon type="Close" />
        </Pressable>
      </XStack>
      {filterTabs.length > 0 && (
        <FilterTabs
          tabs={filterTabs}
          selectedTab={selectedTab}
          onTabSelect={setSelectedTab}
          filterCounts={filterCounts}
        />
      )}
    </Stack>
  )

  const Content = () => (
    <Stack className="px-3 pb-5">{renderFilterContent()}</Stack>
  )

  const Actions = () => (
    <BottomSheetActions onReset={handleResetAll} onApply={handleApply} />
  )

  // Web implementation
  if (Platform.OS === 'web') {
    if (!WebBottomSheet) {
      return null // Component not loaded yet
    }

    return (
      <WebBottomSheet
        open={isVisible}
        onDismiss={handleDismiss}
        snapPoints={({
          maxHeight,
          minHeight,
        }: {
          maxHeight: number
          minHeight: number
        }) => {
          const contentHeight = minHeight
          const maxAllowed = maxHeight * 0.9
          return [Math.min(contentHeight, maxAllowed)]
        }}
        header={<Header />}
        footer={<Actions />}
      >
        <Content />
      </WebBottomSheet>
    )
  }

  // Mobile implementation
  return (
    <BottomSheetModal
      ref={bottomSheetModalRef}
      enableDynamicSizing
      maxDynamicContentSize={height * 0.9}
      backdropComponent={renderBackdrop}
      onDismiss={handleDismiss}
      enablePanDownToClose={true}
    >
      <Header />
      <BottomSheetScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 20 }}
      >
        {renderFilterContent()}
      </BottomSheetScrollView>
      <Actions />
    </BottomSheetModal>
  )
}

export default forwardRef(JfsFiltersBottomSheet)
